<script>
import {Chrome} from "@ckpack/vue-color";
import Popover from "primevue/popover";
import {api} from "../services/api";
import {useWidgetConfigurationStore} from "../stores/widgetConfiguration";
import {
    PhChatCircle,
    PhChatText,
    PhQuestion,
    PhQuestionMark,
    PhEnvelopeSimple,
    PhPhone,
    PhSparkle,
    PhStudent,
    PhPaperPlaneTilt,
    PhBell,
    PhBookOpen,
    PhCalendar,
    PhCheckCircle,
    PhClock,
    PhCode,
    PhCompass,
    PhCurrencyDollar,
    PhGraduationCap,
    PhHeadset,
    PhHeart,
    PhImage,
    PhInfo,
    PhLightbulb,
    PhLink,
    PhListChecks,
    PhMagnifyingGlass,
    PhMapPin,
    PhMegaphone,
    PhNotePencil,
    PhUser,
    PhX,
} from "@phosphor-icons/vue";

const iconOptions = [
    {value: "PhChatCircle", label: "Chat Circle", component: PhChatCircle},
    {value: "PhChatText", label: "Chat Text", component: PhChatText},
    {value: "PhQuestion", label: "Question", component: PhQuestion},
    {value: "PhQuestionMark", label: "Question Mark", component: PhQuestionMark},
    {value: "PhEnvelopeSimple", label: "Envelope", component: PhEnvelopeSimple},
    {value: "PhPhone", label: "Phone", component: PhPhone},
    {value: "PhSparkle", label: "Sparkle", component: PhSparkle},
    {value: "PhStudent", label: "Student", component: PhStudent},
    {value: "PhPaperPlaneTilt", label: "Paper Plane", component: PhPaperPlaneTilt},
    {value: "PhBell", label: "Bell", component: PhBell},
    {value: "PhBookOpen", label: "Book Open", component: PhBookOpen},
    {value: "PhCalendar", label: "Calendar", component: PhCalendar},
    {value: "PhCheckCircle", label: "Check Circle", component: PhCheckCircle},
    {value: "PhClock", label: "Clock", component: PhClock},
    {value: "PhCode", label: "Code", component: PhCode},
    {value: "PhCompass", label: "Compass", component: PhCompass},
    {value: "PhCurrencyDollar", label: "Dollar", component: PhCurrencyDollar},
    {value: "PhGraduationCap", label: "Graduation", component: PhGraduationCap},
    {value: "PhHeadset", label: "Headset", component: PhHeadset},
    {value: "PhHeart", label: "Heart", component: PhHeart},
    {value: "PhImage", label: "Image", component: PhImage},
    {value: "PhInfo", label: "Info", component: PhInfo},
    {value: "PhLightbulb", label: "Lightbulb", component: PhLightbulb},
    {value: "PhLink", label: "Link", component: PhLink},
    {value: "PhListChecks", label: "List Checks", component: PhListChecks},
    {value: "PhMagnifyingGlass", label: "Search", component: PhMagnifyingGlass},
    {value: "PhMapPin", label: "Map Pin", component: PhMapPin},
    {value: "PhMegaphone", label: "Megaphone", component: PhMegaphone},
    {value: "PhNotePencil", label: "Note Pencil", component: PhNotePencil},
    {value: "PhUser", label: "User", component: PhUser},
];

const iconComponentMap = iconOptions.reduce((acc, item) => {
    acc[item.value.toLowerCase()] = item.component;
    return acc;
}, {});

const defaultConversationPrompts = [
    {
        text: "Which countries offer the best scholarships for international students?",
        icon: "PhGraduationCap",
    },
    {
        text: "What are the visa requirements for studying abroad in 2026?",
        icon: "PhCompass",
    },
    {
        text: "How can I prepare a strong SOP for overseas universities?",
        icon: "PhNotePencil",
    },
];

const defaultWidgetConfig = {
    color: {
        type: "solid",
        value: "hsl(220 16% 16%)",
        format: "hsl",
    },
    avatar: {
        type: "link",
        value: "/assets/images/human/female/female-1.png",
    },
    enableFullPage: false,
    cornerStyle: "rounded",
    welcomeTitle: "Hello 👋",
    welcomeMessage: "How can I help you today?",
    conversationMessages: structuredClone(defaultConversationPrompts),
    soundNotification: true,
    buttonMascot: {
        enabled: false,
        value: null,
    },
    buttonStyle: {
        size: "md",
        shadow: "md",
    },
    buttonTrigger: {
        type: "icon_text",
        text: "Need help?",
        icon: "PhChatCircle",
    },
    starterMessage: "Hello, how can I help you?",
    inputPlaceholder: "Type your message...",
    footerNote: "AI can make mistakes, please double-check responses.",
    theme: "dark",
    chatBubbleColor: "hsl(222 20% 17%)",
    userBubbleColor: "hsl(217 91% 60%)",
    bubbleStyle: {
        showArrow: true,
    },
    fontFamily: "Poppins",
    rtl: false,
    floatingSide: "right",
    floatingOffset: "24px",
    floatingPosition: {
        right: "24px",
        left: "0px",
        bottom: "24px",
    },
};

export default {
    name: "AiWidgetConfiguration",
    components: {
        Chrome,
        Popover,
    },
    data() {
        return {
            widgetConfig: structuredClone(defaultWidgetConfig),
            widgetFooterInput: "",
            previewUrlInput: "",
            iframeUrl: "",
            previewMode: "desktop",
            isIframeLoading: true,
            avatarSourceMode: "preset",
            importInputKey: 0,
            iconOptions,
            activeColorTarget: "solid",
            iconPickerTarget: "trigger",
            iconPickerMessageIndex: 0,
            configurationOptions: {
                colorType: ["solid", "gradient"],
                avatarType: ["link", "file"],
                cornerStyle: ["rounded", "square", "squircle"],
                buttonTriggerType: ["icon", "icon_text"],
                buttonSize: ["sm", "md", "lg", "xl"],
                buttonShadow: ["none", "sm", "md", "lg"],
                theme: ["light", "dark"],
                fontFamily: ["Poppins", "Inter", "Roboto", "Lato", "Open Sans", "Nunito"],
            },
            colorExamples: [
                "hsl(217 91% 60%)",
                "hsl(164 76% 35%)",
                "hsl(14 100% 57%)",
                "hsl(265 84% 62%)",
                "hsl(332 87% 55%)",
                "hsl(195 84% 48%)",
                "hsl(282 72% 56%)",
                "hsl(42 96% 55%)",
                "hsl(176 72% 40%)",
                "hsl(349 86% 59%)",
                "hsl(231 73% 58%)",
                "hsl(28 92% 52%)",
            ],
            soundNotificationSrc: "/assets/sounds/popup.mp3",
            defaultAvatarOptions: [
                "/assets/images/human/female/female-1.png",
                "/assets/images/human/female/female-2.png",
                "/assets/images/human/female/female-3.png",
                "/assets/images/human/female/female-4.png",
                "/assets/images/human/female/female-5.png",
                "/assets/images/human/female/female-6.png",
                "/assets/images/human/female/female-7.png",
                "/assets/images/human/female/female-8.png",
                "/assets/images/human/female/female-9.png",
                "/assets/images/human/male/avatar-1.png",
                "/assets/images/human/male/avatar-2.png",
                "/assets/images/human/male/avatar-3.png",
                "/assets/images/human/male/avatar-4.png",
                "/assets/images/human/male/avatar-5.png",
                "/assets/images/human/male/avatar-6.png",
                "/assets/images/human/male/avatar-7.png",
                "/assets/images/human/male/avatar-8.png",
                "/assets/images/human/male/avatar-9.png",
                "/assets/images/human/male/avatar-10.png",
                "/assets/images/human/male/avatar-11.png",
                "/assets/images/human/male/avatar-12.png",
                "/assets/images/human/male/avatar-13.png",
                "/assets/images/human/male/avatar-14.png",
                "/assets/images/human/male/avatar-15.png",
                "/assets/images/human/male/avatar-16.png",
            ],
            widgetStatus: false,
            widgetToggleAudio: null,
            footerNoteLinkUrl: "",
            widgetMeta: {
                aiAgentId: null,
                widgetId: null,
                name: "",
                tone: "",
                isActive: true,
            },
            isConfigLoading: false,
            isConfigSaving: false,
        };
    },
    async mounted() {
        this.initWidgetSound();
        this.ensureConversationPrompts();
        await this.bootstrapWidgetConfig();
    },
    beforeUnmount() {
        if (this.widgetToggleAudio) {
            this.widgetToggleAudio.pause();
            this.widgetToggleAudio = null;
        }
    },
    computed: {
        maxConversationMessages() {
            return this.widgetConfig.color.type === "gradient" ? 4 : 3;
        },
        fontPreviewOptions() {
            return [
                {value: "Poppins", label: "Poppins"},
                {value: "Inter", label: "Inter"},
                {value: "Roboto", label: "Roboto"},
                {value: "Lato", label: "Lato"},
                {value: "Open Sans", label: "Open Sans"},
                {value: "Nunito", label: "Nunito"},
            ];
        },
        canAddConversationMessage() {
            return this.widgetConfig.conversationMessages.length < this.maxConversationMessages;
        },
        resolvedAvatarPreview() {
            return this.widgetConfig.avatar.value || "/assets/images/human/female/female-1.png";
        },
        solidColorValue() {
            return Array.isArray(this.widgetConfig.color.value) ? this.widgetConfig.color.value[0] : this.widgetConfig.color.value;
        },
        gradientBaseValue() {
            return Array.isArray(this.widgetConfig.color.value) ? this.widgetConfig.color.value[0] : this.widgetConfig.color.value;
        },
        gradientStartValue() {
            const [gradientStart] = this.deriveGradientColors(this.gradientBaseValue || "hsl(220 16% 16%)");
            return gradientStart;
        },
        gradientEndValue() {
            const [, gradientEnd] = this.deriveGradientColors(this.gradientBaseValue || "hsl(220 16% 16%)");
            return gradientEnd;
        },
        activePopoverColorValue() {
            if (this.activeColorTarget === "solid") {
                return this.solidColorValue;
            }
            if (this.activeColorTarget === "gradientBase") {
                return this.gradientBaseValue;
            }
            if (this.activeColorTarget === "chatBubble") {
                return this.widgetConfig.chatBubbleColor;
            }
            return this.widgetConfig.userBubbleColor;
        },
        floatingOffsetNumber: {
            get() {
                return this.parsePxNumber(this.widgetConfig.floatingOffset, 24);
            },
            set(value) {
                this.widgetConfig.floatingOffset = `${value}px`;
                if (this.widgetConfig.floatingSide === "right") {
                    this.widgetConfig.floatingPosition.right = `${value}px`;
                    this.widgetConfig.floatingPosition.left = "auto";
                } else {
                    this.widgetConfig.floatingPosition.left = `${value}px`;
                    this.widgetConfig.floatingPosition.right = "auto";
                }
            },
        },
        bottomPositionNumber: {
            get() {
                return this.parsePxNumber(this.widgetConfig.floatingPosition.bottom, 24);
            },
            set(value) {
                this.widgetConfig.floatingPosition.bottom = `${value}px`;
            },
        },
        rootCssVariables() {
            const colorValue = this.widgetConfig.color.value;
            const isGradientType = this.widgetConfig.color.type === "gradient";
            const baseColor = Array.isArray(colorValue) ? colorValue[0] : colorValue;
            const [autoGradientStart, autoGradientEnd] = this.deriveGradientColors(baseColor || "hsl(220 16% 16%)");
            const gradientStart = isGradientType ? autoGradientStart : baseColor || "hsl(220 16% 16%)";
            const gradientEnd = isGradientType ? autoGradientEnd : baseColor || "hsl(220 16% 16%)";
            const isMascotEnabled = this.widgetConfig.buttonMascot?.enabled;
            const triggerBackground = isMascotEnabled ? "transparent" : isGradientType ? `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})` : gradientStart;
            const buttonSize = this.widgetConfig.buttonStyle?.size || "md";
            const buttonShadow = isMascotEnabled ? "none" : this.widgetConfig.buttonStyle?.shadow || "md";
            const buttonSizeMap = {
                sm: "42px",
                md: "50px",
                lg: "58px",
                xl: "72px",
            };
            const buttonShadowMap = {
                none: "none",
                sm: "0 2px 8px rgba(18, 18, 24, 0.14)",
                md: "0 4px 12px rgba(18, 18, 24, 0.2)",
                lg: "0 8px 20px rgba(18, 18, 24, 0.28)",
            };
            const buttonShadowHoverMap = {
                none: "none",
                sm: "0 4px 12px rgba(18, 18, 24, 0.18)",
                md: "0 6px 16px rgba(18, 18, 24, 0.28)",
                lg: "0 10px 24px rgba(18, 18, 24, 0.34)",
            };
            const cornerRadiusMap = {
                square: "0px",
                rounded: "12px",
                squircle: "22px",
            };
            const innerCornerRadiusMap = {
                square: "0px",
                rounded: "12px",
                squircle: "14px",
            };
            const bubbleCornerRadiusMap = {
                square: "4px",
                rounded: "10px",
                squircle: "12px",
            };
            const cornerRadius = cornerRadiusMap[this.widgetConfig.cornerStyle] || "12px";
            const innerCornerRadius = innerCornerRadiusMap[this.widgetConfig.cornerStyle] || "12px";
            const bubbleCornerRadius = bubbleCornerRadiusMap[this.widgetConfig.cornerStyle] || "10px";
            const isDarkTheme = this.widgetConfig.theme === "dark";
            const panelBackground = isDarkTheme ? "hsl(0 0% 8%)" : "#ffffff";
            const panelText = isDarkTheme ? "hsl(0 0% 92%)" : "hsl(224 16% 20%)";
            const panelBorder = isDarkTheme ? "hsl(0 0% 18%)" : "hsl(220 16% 88%)";
            const panelMuted = isDarkTheme ? "hsl(0 0% 66%)" : "hsl(224 10% 42%)";
            const headerBaseColor = gradientStart;
            const headerText = this.getReadableTextColor(headerBaseColor);
            const headerMuted = headerText === "#ffffff" ? "rgba(255, 255, 255, 0.88)" : "rgba(17, 17, 17, 0.72)";
            const gradientSurfaceSoft = this.deriveGradientSurfaceSoft(baseColor || "hsl(220 16% 16%)", isDarkTheme);
            const gradientSurfaceBase = isDarkTheme ? "hsl(228 16% 10%)" : "#ffffff";
            const widgetBodyBackground = isGradientType
                ? `linear-gradient(203deg, ${baseColor}, transparent, transparent), linear-gradient(130deg, ${gradientStart}, ${gradientSurfaceSoft}, ${gradientSurfaceBase}, ${gradientSurfaceBase}, ${gradientSurfaceBase})`
                : panelBackground;
            const headerBackground = isGradientType ? "transparent" : headerBaseColor;
            const widgetBodyBorder = isGradientType ? (isDarkTheme ? "rgba(255, 255, 255, 0.24)" : "rgba(17, 17, 17, 0.12)") : panelBorder;
            const gradientConversationBg = isDarkTheme ? "rgba(15, 18, 25, 0.62)" : "rgba(255, 255, 255, 0.6)";
            const gradientConversationText = isDarkTheme ? "hsl(220 18% 82%)" : "hsl(224 10% 36%)";
            const gradientConversationHoverBg = isDarkTheme ? "rgba(28, 34, 46, 0.78)" : "rgba(255, 255, 255, 0.86)";
            const gradientConversationHoverText = isDarkTheme ? "hsl(220 26% 92%)" : "hsl(224 16% 20%)";
            const gradientConversationBorder = isDarkTheme ? "rgba(255, 255, 255, 0.12)" : "rgba(74, 92, 180, 0.2)";

            return {
                "--widget-color-type": this.widgetConfig.color.type,
                "--widget-color-format": this.widgetConfig.color.format,
                "--widget-color-solid": gradientStart,
                "--widget-color-gradient-start": gradientStart,
                "--widget-color-gradient-end": gradientEnd,
                "--widget-trigger-background": triggerBackground,
                "--widget-trigger-size": buttonSizeMap[buttonSize] || "50px",
                "--widget-trigger-shadow": buttonShadowMap[buttonShadow] || buttonShadowMap.md,
                "--widget-trigger-shadow-hover": buttonShadowHoverMap[buttonShadow] || buttonShadowHoverMap.md,
                "--widget-avatar": this.widgetConfig.avatar.value || "",
                "--widget-corner-style": this.widgetConfig.cornerStyle,
                "--widget-corner-radius": cornerRadius,
                "--widget-inner-corner-radius": innerCornerRadius,
                "--widget-bubble-corner-radius": bubbleCornerRadius,
                "--widget-panel-bg": panelBackground,
                "--widget-panel-text": panelText,
                "--widget-panel-border": panelBorder,
                "--widget-panel-muted": panelMuted,
                "--widget-body-bg": widgetBodyBackground,
                "--widget-body-border": widgetBodyBorder,
                "--widget-header-bg": headerBackground,
                "--widget-header-text": headerText,
                "--widget-header-muted": headerMuted,
                "--widget-gradient-conversation-bg": gradientConversationBg,
                "--widget-gradient-conversation-text": gradientConversationText,
                "--widget-gradient-conversation-hover-bg": gradientConversationHoverBg,
                "--widget-gradient-conversation-hover-text": gradientConversationHoverText,
                "--widget-gradient-conversation-border": gradientConversationBorder,
                "--widget-welcome-title": JSON.stringify(this.widgetConfig.welcomeTitle),
                "--widget-welcome-message": JSON.stringify(this.widgetConfig.welcomeMessage),
                "--widget-starter-message": JSON.stringify(this.widgetConfig.starterMessage),
                "--widget-theme": this.widgetConfig.theme,
                "--widget-chat-bubble-color": this.widgetConfig.chatBubbleColor,
                "--widget-user-bubble-color": this.widgetConfig.userBubbleColor,
                "--widget-chat-bubble-text-color": this.getReadableTextColor(this.widgetConfig.chatBubbleColor),
                "--widget-user-bubble-text-color": this.getReadableTextColor(this.widgetConfig.userBubbleColor),
                "--widget-font-family": this.getFontFamilyStack(this.widgetConfig.fontFamily),
                "--widget-floating-right": this.widgetConfig.floatingSide === "right" ? this.widgetConfig.floatingOffset || "24px" : "auto",
                "--widget-floating-left": this.widgetConfig.floatingSide === "left" ? this.widgetConfig.floatingOffset || "24px" : "auto",
                "--widget-floating-bottom": this.widgetConfig.floatingPosition.bottom || "24px",
            };
        },
    },
    watch: {
        rootCssVariables: {
            immediate: true,
            deep: true,
            handler(cssVariables) {
                const root = document.documentElement;
                Object.entries(cssVariables).forEach(([name, value]) => {
                    root.style.setProperty(name, String(value));
                });
            },
        },
        widgetStatus(isOpen) {
            if (!isOpen) {
                return;
            }

            this.$nextTick(() => {
                this.autoResizeFooterTextarea(this.$refs.widgetFooterTextarea);
            });
        },
    },
    methods: {
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }

            if (this.widgetMeta.aiAgentId) {
                this.$router.push(`/ai-agents/${this.widgetMeta.aiAgentId}`);
                return;
            }

            this.$router.push("/ai-agents");
        },
        async bootstrapWidgetConfig() {
            const context = this.resolveWidgetContext();
            if (!context) {
                alert("Widget context is required to open this page.");
                this.$router.push("/ai-agents");
                return;
            }

            this.widgetMeta.aiAgentId = context.aiAgentId;
            this.widgetMeta.widgetId = context.widgetId;
            this.widgetMeta.name = context.widgetName || "";
            this.widgetMeta.isActive = context.isActive !== false;

            await this.loadWidgetConfig();
        },
        resolveWidgetContext() {
            const store = useWidgetConfigurationStore();
            const queryAgentId = typeof this.$route.query.aiAgentId === "string" ? this.$route.query.aiAgentId : "";
            const queryWidgetId = typeof this.$route.query.widgetId === "string" ? this.$route.query.widgetId : "";

            if (queryAgentId && queryWidgetId) {
                store.setContext({
                    aiAgentId: queryAgentId,
                    widgetId: queryWidgetId,
                    widgetName: store.widgetName,
                    isActive: store.isActive,
                });
            }

            if (!store.aiAgentId || !store.widgetId) {
                return null;
            }

            return {
                aiAgentId: store.aiAgentId,
                widgetId: store.widgetId,
                widgetName: store.widgetName,
                isActive: store.isActive,
            };
        },
        hasSettingsJson(settingsJson) {
            if (!settingsJson) {
                return false;
            }

            if (typeof settingsJson === "string") {
                const trimmed = settingsJson.trim();
                if (!trimmed) {
                    return false;
                }

                try {
                    const parsed = JSON.parse(trimmed);
                    return !!parsed && typeof parsed === "object" && Object.keys(parsed).length > 0;
                } catch {
                    return false;
                }
            }

            return typeof settingsJson === "object" && Object.keys(settingsJson).length > 0;
        },
        parseSettingsJson(settingsJson) {
            if (!settingsJson) {
                return null;
            }

            if (typeof settingsJson === "string") {
                try {
                    const parsed = JSON.parse(settingsJson);
                    return parsed && typeof parsed === "object" ? parsed : null;
                } catch {
                    return null;
                }
            }

            return typeof settingsJson === "object" ? settingsJson : null;
        },
        async loadWidgetConfig() {
            if (!this.widgetMeta.widgetId) {
                return;
            }

            this.isConfigLoading = true;
            try {
                const {data} = await api.get(`/public/agents/${this.widgetMeta.widgetId}/config`);
                this.widgetMeta.name = data?.name || this.widgetMeta.name;
                this.widgetMeta.tone = data?.tone || "";
                this.widgetMeta.aiAgentId = data?.aiAgentId || this.widgetMeta.aiAgentId;

                const store = useWidgetConfigurationStore();
                store.setContext({
                    aiAgentId: this.widgetMeta.aiAgentId,
                    widgetId: this.widgetMeta.widgetId,
                    widgetName: this.widgetMeta.name,
                    isActive: this.widgetMeta.isActive,
                });

                if (this.hasSettingsJson(data?.settingsJson)) {
                    const parsedSettings = this.parseSettingsJson(data.settingsJson);
                    if (parsedSettings) {
                        this.mergeWidgetConfig(parsedSettings);
                    } else {
                        this.resetWidgetConfig();
                    }
                } else {
                    this.resetWidgetConfig();
                }
            } catch (error) {
                console.warn("Widget config load failed:", error);
                alert("Widget configuration could not be loaded.");
            } finally {
                this.isConfigLoading = false;
            }
        },
        async saveWidgetConfig() {
            if (this.isConfigSaving || !this.widgetMeta.aiAgentId || !this.widgetMeta.widgetId) {
                return;
            }

            this.isConfigSaving = true;
            try {
                await api.put(`/ai-agents/${this.widgetMeta.aiAgentId}/widgets/${this.widgetMeta.widgetId}`, {
                    name: this.widgetMeta.name || "Widget",
                    isActive: Boolean(this.widgetMeta.isActive),
                    settingsJson: JSON.stringify(this.getWidgetConfigPayload()),
                });

                alert("Widget configuration saved.");
            } catch (error) {
                console.warn("Widget config save failed:", error);
                alert("Widget configuration could not be saved.");
            } finally {
                this.isConfigSaving = false;
            }
        },
        parseColorToRgb(color) {
            const value = String(color || "").trim();
            if (!value) {
                return null;
            }

            if (value.startsWith("#")) {
                const hex = value.slice(1);
                if (hex.length === 3 || hex.length === 4) {
                    const [r, g, b] = hex.split("").map((part) => Number.parseInt(`${part}${part}`, 16));
                    if ([r, g, b].every((part) => Number.isFinite(part))) {
                        return {r, g, b};
                    }
                    return null;
                }

                if (hex.length === 6 || hex.length === 8) {
                    const r = Number.parseInt(hex.slice(0, 2), 16);
                    const g = Number.parseInt(hex.slice(2, 4), 16);
                    const b = Number.parseInt(hex.slice(4, 6), 16);
                    if ([r, g, b].every((part) => Number.isFinite(part))) {
                        return {r, g, b};
                    }
                }
                return null;
            }

            const rgbMatch = value.match(/^rgba?\((.+)\)$/i);
            if (rgbMatch) {
                const parts = rgbMatch[1]
                    .replace(/\//g, " ")
                    .split(/[\s,]+/)
                    .filter(Boolean)
                    .slice(0, 3);

                if (parts.length === 3) {
                    const rgb = parts.map((part) => {
                        if (part.endsWith("%")) {
                            const percent = Number.parseFloat(part);
                            return Math.round((Math.min(100, Math.max(0, percent)) / 100) * 255);
                        }
                        return Number.parseInt(part, 10);
                    });

                    if (rgb.every((part) => Number.isFinite(part))) {
                        return {
                            r: Math.min(255, Math.max(0, rgb[0])),
                            g: Math.min(255, Math.max(0, rgb[1])),
                            b: Math.min(255, Math.max(0, rgb[2])),
                        };
                    }
                }
                return null;
            }

            const hslMatch = value.match(/^hsla?\((.+)\)$/i);
            if (hslMatch) {
                const rawParts = hslMatch[1].split("/")[0]?.trim() || "";
                let parts = rawParts
                    .split(/[\s,]+/)
                    .filter(Boolean)
                    .slice(0, 3);

                if (parts.length < 3) {
                    parts = rawParts.replace(/\s+/g, "").split(",").filter(Boolean).slice(0, 3);
                }

                if (parts.length >= 3) {
                    const h = Number.parseFloat(parts[0]);
                    const saturationRaw = parts[1].replace("%", "");
                    const lightnessRaw = parts[2].replace("%", "");
                    const s = Number.parseFloat(saturationRaw);
                    const l = Number.parseFloat(lightnessRaw);

                    if ([h, s, l].every((part) => Number.isFinite(part))) {
                        return this.hslToRgb(h, s / 100, l / 100);
                    }
                }
            }

            return null;
        },
        hslToRgb(h, s, l) {
            const hue = ((h % 360) + 360) % 360;
            const chroma = (1 - Math.abs(2 * l - 1)) * s;
            const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
            const m = l - chroma / 2;
            let red = 0;
            let green = 0;
            let blue = 0;

            if (hue < 60) {
                red = chroma;
                green = x;
            } else if (hue < 120) {
                red = x;
                green = chroma;
            } else if (hue < 180) {
                green = chroma;
                blue = x;
            } else if (hue < 240) {
                green = x;
                blue = chroma;
            } else if (hue < 300) {
                red = x;
                blue = chroma;
            } else {
                red = chroma;
                blue = x;
            }

            return {
                r: Math.round((red + m) * 255),
                g: Math.round((green + m) * 255),
                b: Math.round((blue + m) * 255),
            };
        },
        rgbToHsl({r, g, b}) {
            const red = r / 255;
            const green = g / 255;
            const blue = b / 255;
            const max = Math.max(red, green, blue);
            const min = Math.min(red, green, blue);
            const delta = max - min;
            let hue = 0;

            if (delta !== 0) {
                if (max === red) {
                    hue = ((green - blue) / delta) % 6;
                } else if (max === green) {
                    hue = (blue - red) / delta + 2;
                } else {
                    hue = (red - green) / delta + 4;
                }
                hue *= 60;
                if (hue < 0) {
                    hue += 360;
                }
            }

            const lightness = (max + min) / 2;
            const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

            return {
                h: hue,
                s: saturation * 100,
                l: lightness * 100,
            };
        },
        deriveGradientColors(baseColor) {
            const rgb = this.parseColorToRgb(baseColor);
            if (!rgb) {
                return ["hsl(220 86% 58%)", "hsl(220 76% 42%)"];
            }

            const {h, s, l} = this.rgbToHsl(rgb);
            const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
            const startLightness = clamp(l + 14, 10, 92);
            const endLightness = clamp(l - 10, 6, 78);
            const startSaturation = clamp(s + 8, 20, 100);
            const endSaturation = clamp(s - 6, 10, 100);
            const hue = Math.round(h);

            return [`hsl(${hue} ${Math.round(startSaturation)}% ${Math.round(startLightness)}%)`, `hsl(${hue} ${Math.round(endSaturation)}% ${Math.round(endLightness)}%)`];
        },
        deriveGradientSurfaceSoft(baseColor, isDarkTheme) {
            const rgb = this.parseColorToRgb(baseColor);
            if (!rgb) {
                return isDarkTheme ? "hsl(228 18% 20%)" : "hsl(225 100% 92%)";
            }

            const {h, s, l} = this.rgbToHsl(rgb);
            const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
            const hue = Math.round(h);

            if (isDarkTheme) {
                const saturation = clamp(s * 0.38, 16, 48);
                const lightness = clamp(l - 24, 10, 30);
                return `hsl(${hue} ${Math.round(saturation)}% ${Math.round(lightness)}%)`;
            }

            const saturation = clamp(s * 0.5, 20, 56);
            const lightness = clamp(l + 34, 78, 96);
            return `hsl(${hue} ${Math.round(saturation)}% ${Math.round(lightness)}%)`;
        },
        getRelativeLuminance({r, g, b}) {
            const toLinear = (channel) => {
                const normalized = channel / 255;
                return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
            };

            const red = toLinear(r);
            const green = toLinear(g);
            const blue = toLinear(b);
            return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
        },
        getReadableTextColor(backgroundColor) {
            const rgb = this.parseColorToRgb(backgroundColor);
            if (!rgb) {
                return "#ffffff";
            }

            const luminance = this.getRelativeLuminance(rgb);
            return luminance <= 0.42 ? "#ffffff" : "#111111";
        },
        parsePxNumber(value, fallback) {
            const number = Number.parseInt(String(value || "").replace("px", ""), 10);
            return Number.isFinite(number) ? number : fallback;
        },
        getFontFamilyStack(fontName) {
            const fontMap = {
                Inter: "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
                Poppins: "'Poppins', ui-sans-serif, system-ui, -apple-system, sans-serif",
                Roboto: "'Roboto', ui-sans-serif, system-ui, -apple-system, sans-serif",
                Lato: "'Lato', ui-sans-serif, system-ui, -apple-system, sans-serif",
                "Open Sans": "'Open Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
                Nunito: "'Nunito', ui-sans-serif, system-ui, -apple-system, sans-serif",
            };

            return fontMap[fontName] || fontMap.Inter;
        },
        clampPosition(value) {
            return Math.min(120, Math.max(0, Number(value) || 0));
        },
        exportWidgetConfig() {
            try {
                const payload = JSON.stringify(this.getWidgetConfigPayload(), null, 2);
                const blob = new Blob([payload], {type: "application/json"});
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "widget-theme-config.json";
                link.click();
                URL.revokeObjectURL(url);
            } catch (error) {
                console.warn("Widget config export failed:", error);
            }
        },
        triggerImportConfig() {
            this.$refs.configImportInput?.click();
        },
        handleImportConfigChange(event) {
            const file = event.target.files?.[0];
            if (!file) {
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                try {
                    const parsed = JSON.parse(String(reader.result || "{}"));
                    this.mergeWidgetConfig(parsed);
                } catch (error) {
                    console.warn("Widget config import failed:", error);
                } finally {
                    this.importInputKey += 1;
                }
            };
            reader.readAsText(file);
        },
        mergeWidgetConfig(rawConfig) {
            if (!rawConfig || typeof rawConfig !== "object") {
                return;
            }

            const fallback = structuredClone(defaultWidgetConfig);
            const next = {
                ...fallback,
                ...rawConfig,
                color: {...fallback.color, ...(rawConfig.color || {})},
                avatar: {...fallback.avatar, ...(rawConfig.avatar || {})},
                bubbleStyle: {...fallback.bubbleStyle, ...(rawConfig.bubbleStyle || {})},
                buttonMascot: {...fallback.buttonMascot, ...(rawConfig.buttonMascot || {})},
                buttonStyle: {...fallback.buttonStyle, ...(rawConfig.buttonStyle || {})},
                buttonTrigger: {...fallback.buttonTrigger, ...(rawConfig.buttonTrigger || {})},
                floatingSide: rawConfig.floatingSide || fallback.floatingSide || "right",
                floatingOffset: rawConfig.floatingOffset || fallback.floatingOffset || "24px",
                floatingPosition: {...fallback.floatingPosition, ...(rawConfig.floatingPosition || {})},
                conversationMessages: Array.isArray(rawConfig.conversationMessages) ? rawConfig.conversationMessages.slice(0, 4) : fallback.conversationMessages,
            };

            this.widgetConfig = next;
            this.ensureConversationPrompts();
            if (next.avatar?.type === "file") {
                this.avatarSourceMode = "upload";
            } else if (next.avatar?.type === "link" && this.defaultAvatarOptions.includes(next.avatar?.value)) {
                this.avatarSourceMode = "preset";
            } else {
                this.avatarSourceMode = "link";
            }
        },
        ensureConversationPrompts() {
            const current = Array.isArray(this.widgetConfig.conversationMessages) ? [...this.widgetConfig.conversationMessages] : [];
            const next = current.slice(0, this.maxConversationMessages);

            defaultConversationPrompts.forEach((prompt) => {
                if (next.length < this.maxConversationMessages) {
                    next.push(structuredClone(prompt));
                }
            });

            if (next.length > 0) {
                this.widgetConfig.conversationMessages = next;
            }
        },
        resetWidgetConfig() {
            this.widgetConfig = structuredClone(defaultWidgetConfig);
            this.avatarSourceMode = "preset";
        },
        sanitizeForJson(value, seen = new WeakSet()) {
            if (value === null) {
                return null;
            }

            const valueType = typeof value;
            if (valueType === "string" || valueType === "number" || valueType === "boolean") {
                return value;
            }

            if (valueType === "undefined" || valueType === "function" || valueType === "symbol") {
                return undefined;
            }

            if (valueType !== "object") {
                return undefined;
            }

            if (seen.has(value)) {
                return undefined;
            }
            seen.add(value);

            if (Array.isArray(value)) {
                return value.map((item) => this.sanitizeForJson(item, seen)).filter((item) => item !== undefined);
            }

            const output = {};
            Object.entries(value).forEach(([key, item]) => {
                const sanitized = this.sanitizeForJson(item, seen);
                if (sanitized !== undefined) {
                    output[key] = sanitized;
                }
            });

            return output;
        },
        getWidgetConfigPayload() {
            const fallback = structuredClone(defaultWidgetConfig);
            const safeRaw = this.sanitizeForJson(this.widgetConfig) || {};

            return {
                ...fallback,
                ...safeRaw,
                color: {...fallback.color, ...(safeRaw.color || {})},
                avatar: {...fallback.avatar, ...(safeRaw.avatar || {})},
                bubbleStyle: {...fallback.bubbleStyle, ...(safeRaw.bubbleStyle || {})},
                buttonMascot: {...fallback.buttonMascot, ...(safeRaw.buttonMascot || {})},
                buttonStyle: {...fallback.buttonStyle, ...(safeRaw.buttonStyle || {})},
                buttonTrigger: {...fallback.buttonTrigger, ...(safeRaw.buttonTrigger || {})},
                floatingPosition: {...fallback.floatingPosition, ...(safeRaw.floatingPosition || {})},
                conversationMessages: Array.isArray(safeRaw.conversationMessages) ? safeRaw.conversationMessages.slice(0, 4) : fallback.conversationMessages,
            };
        },
        normalizePreviewUrl(rawUrl) {
            const value = (rawUrl || "").trim();
            if (!value) {
                return "";
            }

            const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;

            try {
                const parsed = new URL(withProtocol);
                return parsed.toString();
            } catch {
                return "";
            }
        },
        submitPreviewUrl() {
            const nextUrl = this.normalizePreviewUrl(this.previewUrlInput);
            if (!nextUrl) {
                return;
            }

            this.previewUrlInput = nextUrl;
            this.isIframeLoading = true;
            this.iframeUrl = nextUrl;
        },
        handleIframeLoaded() {
            this.isIframeLoading = false;
        },
        formatColorFromPayload(payload) {
            if (typeof payload === "string") {
                return payload;
            }

            if (payload?.hsl && typeof payload.hsl === "string") {
                return payload.hsl;
            }

            if (!payload || !payload.hex || !payload.hsl) {
                return "hsl(220 16% 16%)";
            }

            const {h, s, l, a} = payload.hsl;
            const hue = Math.round(h || 0);
            const saturation = Math.round((s || 0) * 100);
            const lightness = Math.round((l || 0) * 100);

            if ((a || 1) < 1) {
                return `hsl(${hue} ${saturation}% ${lightness}% / ${(a || 0).toFixed(2)})`;
            }

            return `hsl(${hue} ${saturation}% ${lightness}%)`;
        },
        openColorPopover(event, target) {
            this.activeColorTarget = target;
            this.$refs.colorPopover?.toggle(event);
        },
        handlePopoverColorPicked(payload) {
            const nextColor = this.formatColorFromPayload(payload);

            if (this.activeColorTarget === "solid") {
                this.widgetConfig.color.value = nextColor;
                return;
            }
            if (this.activeColorTarget === "gradientBase") {
                this.widgetConfig.color.value = nextColor;
                return;
            }
            if (this.activeColorTarget === "chatBubble") {
                this.widgetConfig.chatBubbleColor = nextColor;
                return;
            }
            this.widgetConfig.userBubbleColor = nextColor;
        },
        setColorType(type) {
            this.widgetConfig.color.type = type;
            if (type === "gradient" && Array.isArray(this.widgetConfig.color.value)) {
                this.widgetConfig.color.value = this.widgetConfig.color.value[0] || "hsl(220 16% 16%)";
            }
            if (type === "solid" && Array.isArray(this.widgetConfig.color.value)) {
                this.widgetConfig.color.value = this.widgetConfig.color.value[0] || "hsl(220 16% 16%)";
            }

            const limit = type === "gradient" ? 4 : 3;
            if (this.widgetConfig.conversationMessages.length > limit) {
                this.widgetConfig.conversationMessages = this.widgetConfig.conversationMessages.slice(0, limit);
            }
        },
        handleThemeBaseColorPicked(colorValue) {
            this.widgetConfig.color.value = this.formatColorFromPayload(colorValue);
        },
        handleSolidColorPicked(payload) {
            this.widgetConfig.color.value = this.formatColorFromPayload(payload);
        },
        setAvatarMode(mode) {
            this.avatarSourceMode = mode;
            if (mode === "preset") {
                this.widgetConfig.avatar.type = "link";
                if (!this.defaultAvatarOptions.includes(this.widgetConfig.avatar.value)) {
                    this.widgetConfig.avatar.value = this.defaultAvatarOptions[0];
                }
            }
            if (mode === "link") {
                this.widgetConfig.avatar.type = "link";
            }
            if (mode === "upload") {
                this.widgetConfig.avatar.type = "file";
            }
        },
        selectPresetAvatar(path) {
            this.widgetConfig.avatar.type = "link";
            this.widgetConfig.avatar.value = path;
            this.avatarSourceMode = "preset";
        },
        onAvatarFileChange(event) {
            const file = event.target.files?.[0];
            if (!file) {
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                this.widgetConfig.avatar.type = "file";
                this.widgetConfig.avatar.value = String(reader.result || "");
                this.avatarSourceMode = "upload";
            };
            reader.readAsDataURL(file);
        },
        addConversationMessage() {
            if (!this.canAddConversationMessage) {
                return;
            }
            this.widgetConfig.conversationMessages.push({
                text: "",
                icon: "PhChatCircle",
            });
        },
        removeConversationMessage(index) {
            this.widgetConfig.conversationMessages.splice(index, 1);
        },
        normalizeIconKey(value) {
            return String(value || "")
                .replace(/[^a-zA-Z]/g, "")
                .toLowerCase();
        },
        getIconComponent(iconName) {
            const key = this.normalizeIconKey(iconName);
            return iconComponentMap[key] || PhChatCircle;
        },
        openIconPopover(event, target, messageIndex = 0) {
            this.iconPickerTarget = target;
            this.iconPickerMessageIndex = messageIndex;
            this.$refs.iconPopover?.toggle(event);
        },
        selectIcon(iconValue) {
            if (this.iconPickerTarget === "trigger") {
                this.widgetConfig.buttonTrigger.icon = iconValue;
            } else {
                const message = this.widgetConfig.conversationMessages[this.iconPickerMessageIndex];
                if (message) {
                    message.icon = iconValue;
                }
            }
            this.$refs.iconPopover?.hide();
        },
        getIconLabel(iconValue) {
            const found = this.iconOptions.find((item) => item.value === iconValue);
            return found?.label || "Select icon";
        },
        toggleWidgetBody() {
            this.widgetStatus = !this.widgetStatus;
            this.playWidgetToggleSound();
        },
        initWidgetSound() {
            try {
                this.widgetToggleAudio = new Audio(this.soundNotificationSrc);
                this.widgetToggleAudio.preload = "auto";
            } catch {
                this.widgetToggleAudio = null;
            }
        },
        playWidgetToggleSound() {
            if (!this.widgetConfig.soundNotification) {
                return;
            }

            try {
                if (!this.widgetToggleAudio) {
                    this.initWidgetSound();
                }

                if (!this.widgetToggleAudio) {
                    return;
                }

                this.widgetToggleAudio.currentTime = 0;
                this.widgetToggleAudio.play().catch((error) => {
                    // Keep UX silent but expose reason in console for debugging.
                    console.warn("Widget toggle sound could not be played:", error);
                });
            } catch (error) {
                console.warn("Widget sound initialization failed:", error);
            }
        },
        handleFooterInput(event) {
            this.widgetFooterInput = event.target.value;
            this.autoResizeFooterTextarea(event.target);
        },
        autoResizeFooterTextarea(textareaElement) {
            if (!textareaElement) {
                return;
            }

            const maxHeight = 120;
            textareaElement.style.height = "auto";
            textareaElement.style.height = `${Math.min(textareaElement.scrollHeight, maxHeight)}px`;
            textareaElement.style.overflowY = textareaElement.scrollHeight > maxHeight ? "auto" : "hidden";
        },
        insertFooterLink() {
            const url = this.footerNoteLinkUrl.trim();
            if (!url) return;
            const textarea = this.$refs.footerNoteTextarea;
            if (!textarea) return;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = this.widgetConfig.footerNote.substring(start, end);
            if (!selectedText) return;
            const link = `<a href="${url}" target="_blank" rel="noopener noreferrer">${selectedText}</a>`;
            this.widgetConfig.footerNote = this.widgetConfig.footerNote.substring(0, start) + link + this.widgetConfig.footerNote.substring(end);
            this.footerNoteLinkUrl = "";
            this.$nextTick(() => textarea.focus());
        },
    },
};
</script>

<template>
    <div class="config-banner">
        <button type="button" class="banner-btn is-secondary banner-back-btn" @click="goBack">← Back</button>
        <div class="config-banner-title-wrap">
            <h1>Chat Widget Configuration</h1>
            <p>
                {{ widgetMeta.name || "Widget" }}
                <span v-if="widgetMeta.tone"> · Tone: {{ widgetMeta.tone }}</span>
            </p>
        </div>
        <div class="config-banner-actions">
            <button type="button" class="banner-btn is-secondary" @click="triggerImportConfig">Import JSON</button>
            <button type="button" class="banner-btn is-secondary" @click="exportWidgetConfig">Export JSON</button>
            <button type="button" class="banner-btn is-secondary" :disabled="isConfigSaving || isConfigLoading" @click="saveWidgetConfig">
                {{ isConfigSaving ? "Saving..." : "Save" }}
            </button>
            <button type="button" class="banner-btn is-primary" @click="resetWidgetConfig">Reset to Default</button>
            <input :key="importInputKey" ref="configImportInput" type="file" accept="application/json" class="config-import-input" @change="handleImportConfigChange" />
        </div>
    </div>
    <div class="config">
        <div class="config-settings">
            <h2>Widget Settings</h2>

            <details class="settings-block" open>
                <summary>General</summary>
                <div class="settings-grid">
                    <div class="settings-group">
                        <span class="settings-group-title">Theme</span>
                        <div class="theme-card-grid">
                            <button
                                v-for="item in configurationOptions.theme"
                                :key="item"
                                type="button"
                                :class="['theme-card', `is-${item}`, {active: widgetConfig.theme === item}]"
                                @click="widgetConfig.theme = item"
                            >
                                <span class="theme-card-preview">
                                    <em></em>
                                    <small></small>
                                </span>
                                <strong>{{ item }}</strong>
                            </button>
                        </div>
                    </div>

                    <div class="settings-group">
                        <span class="settings-group-title">Font Family</span>
                        <div class="font-card-grid">
                            <button
                                v-for="item in fontPreviewOptions"
                                :key="item.value"
                                type="button"
                                :class="['font-card', {active: widgetConfig.fontFamily === item.value}]"
                                :style="{fontFamily: getFontFamilyStack(item.value)}"
                                @click="widgetConfig.fontFamily = item.value"
                            >
                                {{ item.label }}
                            </button>
                        </div>
                    </div>

                    <label class="switch-row">
                        <span>Enable Full Page</span>
                        <input v-model="widgetConfig.enableFullPage" type="checkbox" />
                        <em></em>
                    </label>
                    <label class="switch-row">
                        <span>Sound Notification</span>
                        <input v-model="widgetConfig.soundNotification" type="checkbox" />
                        <em></em>
                    </label>
                    <label class="switch-row">
                        <span>Bubble Arrow</span>
                        <input v-model="widgetConfig.bubbleStyle.showArrow" type="checkbox" />
                        <em></em>
                    </label>
                    <label class="switch-row">
                        <span>RTL Direction</span>
                        <input v-model="widgetConfig.rtl" type="checkbox" />
                        <em></em>
                    </label>
                </div>
            </details>

            <details class="settings-block" open>
                <summary>Colors</summary>
                <div class="settings-grid">
                    <div class="settings-group">
                        <span class="settings-group-title">Color Type</span>
                        <div class="option-chip-row">
                            <button
                                v-for="item in configurationOptions.colorType"
                                :key="item"
                                type="button"
                                :class="['option-chip', {active: widgetConfig.color.type === item}]"
                                @click="setColorType(item)"
                            >
                                {{ item }}
                            </button>
                        </div>
                    </div>

                    <div class="color-examples">
                        <a role="button" v-for="sample in colorExamples" :key="sample" :style="{background: sample}" @click="handleThemeBaseColorPicked(sample)"></a>
                    </div>

                    <div class="gradient-pickers" v-if="widgetConfig.color.type === 'solid'">
                        <div class="color-input-row">
                            <strong>Color Base</strong>
                            <div class="color-input-row-inner">
                                <input v-model="widgetConfig.color.value" type="text" placeholder="hsl(217 91% 60%)" />
                                <button type="button" class="color-dot-button" :style="{background: solidColorValue}" @click="openColorPopover($event, 'solid')"></button>
                            </div>
                        </div>
                    </div>

                    <div class="gradient-pickers" v-else>
                        <div class="color-input-row">
                            <strong>Gradient Base</strong>
                            <div class="color-input-row-inner">
                                <input :value="gradientBaseValue" type="text" readonly />
                                <button type="button" class="color-dot-button" :style="{background: gradientBaseValue}" @click="openColorPopover($event, 'gradientBase')"></button>
                            </div>
                        </div>
                    </div>

                    <label>
                        Chat Bubble
                        <div class="color-input-row-inner">
                            <input v-model="widgetConfig.chatBubbleColor" type="text" placeholder="hsl(0 0% 12%)" readonly />
                            <button type="button" class="color-dot-button" :style="{background: widgetConfig.chatBubbleColor}" @click="openColorPopover($event, 'chatBubble')"></button>
                        </div>
                    </label>

                    <label>
                        User Bubble
                        <div class="color-input-row-inner">
                            <input v-model="widgetConfig.userBubbleColor" type="text" placeholder="hsl(217 91% 60%)" readonly />
                            <button type="button" class="color-dot-button" :style="{background: widgetConfig.userBubbleColor}" @click="openColorPopover($event, 'userBubble')"></button>
                        </div>
                    </label>
                </div>
            </details>

            <details class="settings-block" open>
                <summary>Corner Style</summary>
                <div class="corner-options">
                    <button
                        v-for="item in configurationOptions.cornerStyle"
                        :key="item"
                        type="button"
                        :class="['corner-card', {active: widgetConfig.cornerStyle === item}]"
                        @click="widgetConfig.cornerStyle = item"
                    >
                        <span class="corner-preview" :class="`is-${item}`"></span>
                        <strong>{{ item }}</strong>
                    </button>
                </div>
            </details>

            <details class="settings-block" open>
                <summary>Avatar</summary>
                <div class="avatar-mode-tabs">
                    <button type="button" :class="{active: avatarSourceMode === 'preset'}" @click="setAvatarMode('preset')">Default</button>
                    <button type="button" :class="{active: avatarSourceMode === 'link'}" @click="setAvatarMode('link')">Link</button>
                    <button type="button" :class="{active: avatarSourceMode === 'upload'}" @click="setAvatarMode('upload')">Upload</button>
                </div>

                <div v-if="avatarSourceMode === 'preset'" class="avatar-grid">
                    <button v-for="item in defaultAvatarOptions" :key="item" type="button" :class="['avatar-choice', {active: widgetConfig.avatar.value === item}]" @click="selectPresetAvatar(item)">
                        <img :src="item" alt="avatar" />
                    </button>
                </div>

                <div v-if="avatarSourceMode === 'link'" class="settings-grid">
                    <label>
                        Avatar URL
                        <input v-model="widgetConfig.avatar.value" type="text" placeholder="https://cdn.site/avatar.png" />
                    </label>
                </div>

                <div v-if="avatarSourceMode === 'upload'" class="settings-grid">
                    <label>
                        Upload Avatar
                        <input type="file" accept="image/*" @change="onAvatarFileChange" />
                    </label>
                </div>
            </details>

            <details class="settings-block" open>
                <summary>Messages</summary>
                <div class="settings-grid">
                    <label>
                        Welcome Title
                        <input v-model="widgetConfig.welcomeTitle" type="text" />
                    </label>
                    <label>
                        Welcome Message
                        <textarea v-model="widgetConfig.welcomeMessage" rows="2"></textarea>
                    </label>
                    <label>
                        Starter Message
                        <textarea v-model="widgetConfig.starterMessage" rows="2"></textarea>
                    </label>
                    <label>
                        Input Placeholder
                        <input v-model="widgetConfig.inputPlaceholder" type="text" placeholder="Type your message..." />
                    </label>
                    <div class="footer-note-editor">
                        <label>
                            Footer Note
                            <textarea ref="footerNoteTextarea" v-model="widgetConfig.footerNote" rows="2" placeholder="e.g. AI can make mistakes, please double-check responses."></textarea>
                        </label>
                        <div class="footer-note-link-row">
                            <input v-model="footerNoteLinkUrl" type="url" placeholder="https://example.com" @keydown.enter.prevent="insertFooterLink" />
                            <button type="button" @click="insertFooterLink" :disabled="!footerNoteLinkUrl.trim()">Add Link to Selection</button>
                        </div>
                        <small class="footer-note-hint">Select text above, then enter a URL and click "Add Link".</small>
                    </div>
                </div>

                <div class="conversation-list">
                    <div class="list-head">
                        <div>
                            <strong>Conversation Messages</strong>
                            <small>Max {{ maxConversationMessages }} messages</small>
                        </div>
                        <button type="button" :disabled="!canAddConversationMessage" @click="addConversationMessage">+ Add</button>
                    </div>

                    <div v-for="(message, index) in widgetConfig.conversationMessages" :key="index" class="conversation-item">
                        <button type="button" class="icon-dropdown-button" @click="openIconPopover($event, 'message', index)" style="display: flex; align-items: center; padding: 0.5rem">
                            <component :is="getIconComponent(message.icon)" :size="18" weight="duotone" />
                            <!-- {{ getIconLabel(message.icon) }} -->
                        </button>
                        <input v-model="message.text" type="text" placeholder="Message text" />

                        <button type="button" class="remove-btn" @click="removeConversationMessage(index)">Remove</button>
                    </div>
                </div>
            </details>

            <details class="settings-block" open>
                <summary>Button And Position</summary>
                <div class="settings-grid">
                    <div class="settings-group">
                        <span class="settings-group-title">Trigger Type</span>
                        <div class="option-chip-row">
                            <button
                                v-for="item in configurationOptions.buttonTriggerType"
                                :key="item"
                                type="button"
                                :class="['option-chip', {active: widgetConfig.buttonTrigger.type === item}]"
                                @click="widgetConfig.buttonTrigger.type = item"
                            >
                                {{ item }}
                            </button>
                        </div>
                    </div>
                    <label>
                        Trigger Text
                        <input v-model="widgetConfig.buttonTrigger.text" type="text" />
                    </label>
                    <label v-if="!widgetConfig.buttonMascot.enabled">
                        Trigger Icon
                        <button type="button" class="icon-dropdown-button" @click="openIconPopover($event, 'trigger')" style="display: flex; align-items: center; gap: 0.5rem">
                            <component :is="getIconComponent(widgetConfig.buttonTrigger.icon)" :size="18" weight="duotone" />
                            {{ getIconLabel(widgetConfig.buttonTrigger.icon) }}
                        </button>
                    </label>
                    <div class="settings-group">
                        <span class="settings-group-title">Button Size</span>
                        <div class="option-chip-row">
                            <button
                                v-for="item in configurationOptions.buttonSize"
                                :key="item"
                                type="button"
                                :class="['option-chip', {active: widgetConfig.buttonStyle.size === item}]"
                                @click="widgetConfig.buttonStyle.size = item"
                            >
                                {{ item }}
                            </button>
                        </div>
                    </div>
                    <div class="settings-group">
                        <span class="settings-group-title">Button Shadow</span>
                        <div class="option-chip-row">
                            <button
                                v-for="item in configurationOptions.buttonShadow"
                                :key="item"
                                type="button"
                                :class="['option-chip', {active: widgetConfig.buttonStyle.shadow === item}]"
                                @click="widgetConfig.buttonStyle.shadow = item"
                            >
                                {{ item }}
                            </button>
                        </div>
                    </div>
                    <label class="switch-row">
                        <span>Mascot Enabled</span>
                        <input v-model="widgetConfig.buttonMascot.enabled" type="checkbox" />
                        <em></em>
                    </label>
                    <label v-if="widgetConfig.buttonMascot.enabled">
                        Mascot URL
                        <input v-model="widgetConfig.buttonMascot.value" type="text" placeholder="https://cdn.site/mascot.png" />
                    </label>
                    <div class="settings-group">
                        <span class="settings-group-title">Position</span>
                        <div class="option-chip-row">
                            <button
                                type="button"
                                :class="['option-chip', {active: widgetConfig.floatingSide === 'right'}]"
                                @click="
                                    widgetConfig.floatingSide = 'right';
                                    floatingOffsetNumber = floatingOffsetNumber;
                                "
                            >
                                Right
                            </button>
                            <button
                                type="button"
                                :class="['option-chip', {active: widgetConfig.floatingSide === 'left'}]"
                                @click="
                                    widgetConfig.floatingSide = 'left';
                                    floatingOffsetNumber = floatingOffsetNumber;
                                "
                            >
                                Left
                            </button>
                        </div>
                    </div>
                    <div class="position-control">
                        <label>
                            {{ widgetConfig.floatingSide === "right" ? "Right" : "Left" }} (px)
                            <input type="number" min="0" max="120" :value="floatingOffsetNumber" @input="floatingOffsetNumber = clampPosition($event.target.value)" />
                        </label>
                        <input type="range" min="0" max="120" :value="floatingOffsetNumber" @input="floatingOffsetNumber = clampPosition($event.target.value)" />
                    </div>
                    <div class="position-control">
                        <label>
                            Bottom (px)
                            <input type="number" min="0" max="120" :value="bottomPositionNumber" @input="bottomPositionNumber = clampPosition($event.target.value)" />
                        </label>
                        <input type="range" min="0" max="120" :value="bottomPositionNumber" @input="bottomPositionNumber = clampPosition($event.target.value)" />
                    </div>
                </div>
            </details>

            <Popover ref="colorPopover" class="settings-popover" appendTo="body">
                <div class="color-picker-panel dropdown-color-panel">
                    <Chrome :model-value="activePopoverColorValue" @update:modelValue="handlePopoverColorPicked" />
                </div>
            </Popover>

            <Popover ref="iconPopover" class="settings-popover" appendTo="body">
                <div class="icon-picker-grid">
                    <button v-for="item in iconOptions" :key="item.value" type="button" class="icon-picker-item" @click="selectIcon(item.value)">
                        <component :is="item.component" :size="18" weight="duotone" />
                        <span>{{ item.label }}</span>
                    </button>
                </div>
            </Popover>
        </div>
        <div class="config-window">
            <div class="preview-browser-chrome">
                <div class="preview-browser-left">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <div class="preview-browser-tab">
                    <div class="preview-browser-circle"></div>
                    <div class="preview-browser-pill"></div>
                </div>
            </div>

            <div class="config-window-header">
                <span>Preview on</span>
                <form class="preview-form" @submit.prevent="submitPreviewUrl">
                    <input v-model="previewUrlInput" type="text" placeholder="https://example.com" />
                    <button type="submit" aria-label="Preview URL">→</button>
                </form>
                <div class="preview-mode-toggle">
                    <button type="button" :class="['preview-mode-btn', {active: previewMode === 'desktop'}]" @click="previewMode = 'desktop'" title="Desktop">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor">
                            <rect x="32" y="48" width="192" height="144" rx="16" fill="none" stroke="currentColor" stroke-width="20" />
                            <line x1="96" y1="224" x2="160" y2="224" stroke="currentColor" stroke-width="20" stroke-linecap="round" />
                            <line x1="128" y1="192" x2="128" y2="224" stroke="currentColor" stroke-width="20" stroke-linecap="round" />
                        </svg>
                    </button>
                    <button type="button" :class="['preview-mode-btn', {active: previewMode === 'mobile'}]" @click="previewMode = 'mobile'" title="Mobile">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor">
                            <rect x="64" y="16" width="128" height="224" rx="20" fill="none" stroke="currentColor" stroke-width="20" />
                            <circle cx="128" cy="196" r="10" />
                        </svg>
                    </button>
                </div>
            </div>

            <div class="config-window-preview" :class="{'is-mobile': previewMode === 'mobile'}">
                <div class="preview-frame-wrap">
                    <iframe :key="iframeUrl" :src="iframeUrl" title="Website Preview" @load="handleIframeLoaded"></iframe>

                    <div class="preview-overlay">
                        <div class="widget" :class="{'widget-is-open': widgetStatus}" :dir="widgetConfig.rtl ? 'rtl' : 'ltr'">
                            <div class="widget-trigger">
                                <span
                                    class="widget-trigger-label"
                                    :class="[`is-${widgetConfig.cornerStyle}`, {'bubble-arrow': widgetConfig.bubbleStyle.showArrow}]"
                                    v-html="widgetConfig.buttonTrigger.text"
                                    v-if="!widgetStatus && widgetConfig.buttonTrigger.type !== 'icon' && widgetConfig.buttonTrigger.text"
                                ></span>
                                <button type="button" class="widget-trigger-btn" :class="{'is-open': widgetStatus}" @click="toggleWidgetBody">
                                    <template v-if="!widgetConfig.buttonMascot.enabled">
                                        <PhX :size="20" weight="bold" v-if="widgetStatus" />
                                        <component :is="getIconComponent(widgetConfig.buttonTrigger.icon)" :size="18" weight="fill" v-else />
                                    </template>
                                    <img :src="widgetConfig.buttonMascot.value" alt="mascot" v-else class="widget-trigger-mascot" />
                                </button>
                            </div>
                            <transition name="widget-body-fade">
                                <div
                                    v-if="widgetStatus"
                                    class="widget-body"
                                    :class="[`is-${widgetConfig.cornerStyle}`, `is-color-${widgetConfig.color.type}`, {'bubble-arrow-off': !widgetConfig.bubbleStyle.showArrow}]"
                                >
                                    <button type="button" class="widget-body-close" @click="toggleWidgetBody" aria-label="Close" v-if="previewMode === 'mobile'">
                                        <PhX :size="16" weight="bold" />
                                    </button>
                                    <div class="widget-body-header" :class="`is-color-${widgetConfig.color.type}`">
                                        <img :src="widgetConfig.avatar.value" :alt="widgetConfig.welcomeTitle" class="widget-avatar" />

                                        <h3 class="widget-welcome-title">{{ widgetConfig.welcomeTitle }}</h3>
                                        <p class="widget-welcome-message">{{ widgetConfig.welcomeMessage }}</p>

                                        <template v-if="widgetConfig.conversationMessages && widgetConfig.conversationMessages.length > 0">
                                            <div class="conversation-message-list" :class="`is-color-${widgetConfig.color.type}`">
                                                <a role="button" class="conversation-message-item" v-for="(message, index) in widgetConfig.conversationMessages" :key="index">
                                                    <component :is="getIconComponent(message.icon)" :size="24" />
                                                    <span>{{ message.text }}</span>
                                                </a>
                                            </div>
                                        </template>
                                    </div>
                                    <div class="widget-body-scroll">
                                        <div class="chat-message" v-if="widgetConfig.starterMessage">
                                            <div class="chat-message-avatar">
                                                <img :src="widgetConfig.avatar.value" :alt="widgetConfig.welcomeTitle" />
                                            </div>
                                            <div class="chat-message-data">
                                                <div class="chat-message-data-inner">
                                                    <div class="message-text">
                                                        <p v-html="widgetConfig.starterMessage"></p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="chat-message sender" v-if="widgetConfig.starterMessage">
                                            <div class="chat-message-avatar">
                                                <img :src="widgetConfig.avatar.value" :alt="widgetConfig.welcomeTitle" />
                                            </div>
                                            <div class="chat-message-data">
                                                <div class="chat-message-data-inner">
                                                    <div class="message-text">
                                                        <p v-html="widgetConfig.starterMessage"></p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="widget-body-footer">
                                        <div class="widget-body-footer-row">
                                            <textarea
                                                ref="widgetFooterTextarea"
                                                class="widget-body-footer-input"
                                                :value="widgetFooterInput"
                                                rows="1"
                                                :placeholder="widgetConfig.inputPlaceholder || 'Type your message...'"
                                                @input="handleFooterInput"
                                            ></textarea>
                                            <button type="button" class="widget-body-footer-send" aria-label="Send message">
                                                <PhPaperPlaneTilt :size="16" weight="fill" />
                                            </button>
                                        </div>
                                        <p class="widget-body-footer-note" v-html="widgetConfig.footerNote"></p>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <div v-if="isIframeLoading" class="preview-loading">Loading preview...</div>
                </div>

                <div v-if="isIframeLoading" class="preview-loading">Loading preview...</div>
            </div>

            <p class="preview-note">
                Note: The preview may not reflect all configuration changes in real-time. For the best experience, please integrate the widget into your website and test it there. <br />
                Some sites may not open in the iframe due to X-Frame-Options / CSP.
            </p>
        </div>
    </div>
</template>

<style lang="scss">
body {
    background-color: #fff !important;
    color: #000 !important;
}

.config-banner {
    margin: 1rem 1rem 0;
    padding: 1rem 1.1rem;
    border: 1px solid #e2dfe8;
    background: linear-gradient(135deg, #ffffff 0%, #f5f4f9 100%);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.9rem;
}

.config-banner-title-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.45rem;
    flex-grow: 1;

    h1 {
        margin: 0;
        font-size: 1.15rem;
        color: #1f1f27;
    }

    p {
        margin: 0.2rem 0 0;
        font-size: 0.78rem;
        color: #646172;
    }
}

.banner-back-btn {
    padding: 0.35rem 0.65rem;
    font-size: 0.74rem;
}

.config-banner-actions {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex-wrap: wrap;
}

.banner-btn {
    border: 1px solid #d6d2df;
    border-radius: 0.65rem;
    padding: 0.48rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;

    &.is-secondary {
        background: #fff;
        color: #3b3947;
    }

    &.is-primary {
        background: #17171d;
        color: #fff;
        border-color: #17171d;
    }
}

.config-import-input {
    display: none;
}

.config {
    display: flex;
    height: calc(100vh - 2rem - 90px);
    gap: 1rem;
    margin: 1rem;
    &-settings {
        background-color: #f5f4f7;
        max-width: 400px;
        width: 100%;
        border-radius: 1rem;
        padding: 1.5rem;
        overflow: auto;

        h2 {
            margin-top: 0;
            margin-bottom: 1rem;
            font-size: 1rem;
            color: #222;
        }
    }
    &-window {
        flex: 1;
        background-color: #f5f4f7;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0;
        overflow: hidden;
    }

    &-window-header {
        border-radius: 0;
        background: linear-gradient(90deg, #171717 0%, #1f1f1f 100%);
        color: #fff;
        min-height: 68px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.8rem;
        padding: 0.6rem 1rem;
        font-size: 0.9rem;
        font-weight: 600;
        border-top: 1px solid rgba(255, 255, 255, 0.06);
    }

    &-window-preview {
        position: relative;
        border-radius: 0.8rem;
        overflow: hidden;
        border: 1px solid #dedbe4;
        background: #fff;
        flex: 1;
        margin: 1.5rem;

        iframe {
            width: 100%;
            height: 100%;
            border: 0;
            background: #fff;
        }
    }
}

.settings-block {
    border: 1px solid #e5e2ea;
    border-radius: 0.9rem;
    background: #fff;
    margin-bottom: 0.8rem;
    overflow: hidden;

    summary {
        cursor: pointer;
        padding: 0.9rem 1rem;
        font-weight: 600;
        color: #1f1f25;
        list-style: none;
        border-bottom: 1px solid #efedf3;
    }

    summary::-webkit-details-marker {
        display: none;
    }
}

.settings-grid {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    padding: 1rem;

    label {
        display: grid;
        gap: 0.35rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: #34333c;
    }

    input,
    select,
    textarea {
        border: 1px solid #d7d3de;
        border-radius: 0.7rem;
        background: #fff;
        padding: 0.62rem 0.75rem;
        font-size: 0.84rem;
        color: #25242b;
    }

    textarea {
        resize: vertical;
        min-height: 60px;
    }
}

.footer-note-editor {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
        display: grid;
        gap: 0.35rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: #34333c;
    }

    textarea {
        border: 1px solid #d7d3de;
        border-radius: 0.7rem;
        background: #fff;
        padding: 0.62rem 0.75rem;
        font-size: 0.84rem;
        color: #25242b;
        resize: vertical;
        min-height: 60px;
        font-family: inherit;
    }
}

.footer-note-link-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;

    input[type="url"] {
        flex: 1;
        border: 1px solid #d7d3de;
        border-radius: 0.7rem;
        background: #fff;
        padding: 0.5rem 0.75rem;
        font-size: 0.82rem;
        color: #25242b;
        min-width: 0;
    }

    button {
        white-space: nowrap;
        border: 1px solid #a89ec5;
        background: #f3f0fa;
        color: #4b3d82;
        border-radius: 0.65rem;
        padding: 0.5rem 0.75rem;
        font-size: 0.78rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s;

        &:hover:not(:disabled) {
            background: #e6e0f8;
        }

        &:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }
    }
}

.footer-note-hint {
    font-size: 0.72rem;
    color: #8a85a0;
    padding: 0 0.1rem;
}

.settings-group {
    display: grid;
    gap: 0.45rem;
}

.settings-group-title {
    font-size: 0.78rem;
    font-weight: 700;
    color: #393744;
}

.option-chip-row {
    display: flex;
    gap: 0.45rem;
    flex-wrap: wrap;
}

.option-chip {
    border: 1px solid #d5d2dd;
    background: #f8f7fb;
    color: #302e39;
    border-radius: 0.65rem;
    padding: 0.45rem 0.65rem;
    font-size: 0.76rem;
    text-transform: capitalize;
    cursor: pointer;

    &.active {
        border-color: #3e7ded;
        background: #edf4ff;
        color: #1f4ea6;
    }
}

.theme-card-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
}

.theme-card {
    border: 1px solid #d6d2dd;
    background: #fff;
    border-radius: 0.75rem;
    padding: 0.45rem;
    display: grid;
    gap: 0.45rem;
    cursor: pointer;

    .theme-card-preview {
        display: grid;
        grid-template-columns: 28% 1fr;
        gap: 0.25rem;
        height: 36px;
        border-radius: 0.5rem;
        overflow: hidden;
        border: 1px solid #d8d4e0;

        em,
        small {
            display: block;
        }
    }

    strong {
        font-size: 0.74rem;
        text-transform: capitalize;
        color: #2f2d38;
    }

    &.is-light .theme-card-preview {
        em {
            background: #f3f5fb;
        }

        small {
            background: #ffffff;
        }
    }

    &.is-dark .theme-card-preview {
        em {
            background: #202129;
        }

        small {
            background: #15161c;
        }
    }

    &.active {
        border-color: #3e7ded;
        box-shadow: 0 0 0 2px rgba(62, 125, 237, 0.2);
    }
}

.font-card-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
}

.font-card {
    border: 1px solid #d9d4e2;
    border-radius: 0.7rem;
    background: #fff;
    min-height: 44px;
    padding: 0.45rem 0.6rem;
    text-align: left;
    font-size: 0.92rem;
    cursor: pointer;

    &.active {
        border-color: #1e8bff;
        background: #f2f8ff;
    }
}

.switch-row {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 0.84rem;
    position: relative;

    span {
        color: #454252;
        font-weight: 700;
    }

    input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
    }

    em {
        width: 42px;
        height: 24px;
        border-radius: 999px;
        background: #d9d5e3;
        position: relative;
        transition: 0.2s ease;

        &::after {
            content: "";
            position: absolute;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            left: 3px;
            top: 3px;
            background: #fff;
            transition: 0.2s ease;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
        }
    }

    input:checked + em {
        background: hsl(163 76% 39%);
    }

    input:checked + em::after {
        transform: translateX(18px);
    }
}

.color-examples {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    a {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 0 0 1px #d9d5de;
        justify-self: start;
        cursor: pointer;
    }
}

.color-picker-panel {
    .vc-chrome {
        width: 100%;
        box-shadow: none;
        border: 1px solid #ddd8e5;
        border-radius: 0.8rem;
    }
}

.color-input-row {
    display: grid;
    gap: 0.4rem;
}

.color-input-row-inner {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 0.45rem;
}

.color-dot-button {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px #cac5d4;
    cursor: pointer;
}

.gradient-pickers {
    display: grid;
    // grid-template-columns: 1fr 1fr;
    gap: 0.8rem;

    strong {
        display: block;
        margin-bottom: 0.45rem;
        font-size: 0.76rem;
    }

    .vc-chrome {
        width: 100%;
        box-shadow: none;
        border: 1px solid #ddd8e5;
        border-radius: 0.8rem;
    }
}

.dropdown-color-panel {
    width: 280px;
}

.corner-options {
    padding: 1rem;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.65rem;
}

.corner-card {
    border: 1px solid #d9d5de;
    background: #f3f2f6;
    border-radius: 0.7rem;
    padding: 0.55rem;
    cursor: pointer;
    display: grid;
    gap: 0.45rem;

    strong {
        text-transform: capitalize;
        font-size: 0.76rem;
        color: #3a3942;
    }

    &.active {
        border-color: #1e8bff;
        box-shadow: 0 0 0 2px rgba(30, 139, 255, 0.12);
    }
}

.corner-preview {
    height: 52px;
    width: 100%;
    background: #fff;
    border: 1px solid #e3e0e9;
    display: block;

    &.is-square {
        border-radius: 0.25rem;
    }

    &.is-rounded {
        border-radius: 0.8rem;
    }

    &.is-squircle {
        border-radius: 1.2rem;
    }
}

.avatar-mode-tabs {
    padding: 1rem 1rem 0.7rem;
    display: flex;
    gap: 0.45rem;

    button {
        border: 1px solid #d9d5de;
        background: #f4f2f7;
        border-radius: 0.65rem;
        font-size: 0.8rem;
        padding: 0.48rem 0.75rem;
        cursor: pointer;

        &.active {
            border-color: #1e8bff;
            color: #1e8bff;
            background: #fff;
        }
    }
}

.avatar-grid {
    padding: 0 1rem 1rem;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.45rem;
}

.avatar-choice {
    border: 1px solid #ddd9e4;
    border-radius: 0.6rem;
    padding: 0.2rem;
    background: #fff;
    cursor: pointer;

    img {
        width: 100%;
        border-radius: 0.3rem;
        display: block;
        aspect-ratio: 1;
        object-fit: cover;
    }

    &.active {
        border-color: #1e8bff;
        box-shadow: 0 0 0 2px rgba(30, 139, 255, 0.12);
    }
}

.conversation-list {
    padding: 0 1rem 1rem;

    small {
        display: block;
        margin-bottom: 0.6rem;
        color: #6b6976;
        font-size: 0.72rem;
    }
}

.list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.2rem;

    strong {
        font-size: 0.8rem;
        color: #2c2b33;
    }

    button {
        border: 1px solid #d9d5de;
        background: #fff;
        border-radius: 0.5rem;
        font-size: 0.76rem;
        padding: 0.34rem 0.55rem;
        cursor: pointer;
    }
}

.conversation-item {
    display: grid;
    grid-template-columns: 35px 1fr auto;
    gap: 0.45rem;
    margin-bottom: 0.45rem;

    input {
        border: 1px solid #d7d3de;
        border-radius: 0.62rem;
        padding: 0.55rem 0.65rem;
        font-size: 0.8rem;
    }

    .remove-btn {
        border: 1px solid #f0c9c9;
        background: #fff2f2;
        color: #af3535;
        border-radius: 0.62rem;
        font-size: 0.75rem;
        padding: 0.45rem 0.55rem;
        cursor: pointer;
    }
}

.message-icon-select {
    display: grid;
    grid-template-columns: 22px 1fr;
    align-items: center;
    gap: 0.35rem;
}

.icon-dropdown-button {
    width: 100%;
    border: 1px solid #d7d3de;
    border-radius: 0.62rem;
    background: #fff;
    padding: 0.55rem 0.65rem;
    text-align: left;
    font-size: 0.8rem;
    cursor: pointer;
}

.settings-popover {
    background: #fff;
    border: 1px solid #ddd8e5;
    border-radius: 0.8rem;
    box-shadow: 0 14px 34px rgba(18, 18, 24, 0.2);
    padding: 0.6rem;
    z-index: 5000;
}

.icon-picker-grid {
    width: 320px;
    max-height: 260px;
    overflow: auto;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.4rem;
}

.icon-picker-item {
    border: 1px solid #e5e2ea;
    border-radius: 0.6rem;
    background: #fff;
    padding: 0.45rem;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    font-size: 0.72rem;

    &:hover {
        border-color: #1e8bff;
        background: #f4f8ff;
    }
}

.position-control {
    border: 1px solid #e4e0e9;
    border-radius: 0.7rem;
    padding: 0.6rem;
    background: #fcfbfe;

    label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.4rem;
        font-size: 0.78rem;
    }

    input[type="number"] {
        width: 72px;
    }

    input[type="range"] {
        width: 100%;
    }
}

.preview-browser-chrome {
    background: linear-gradient(90deg, #181818 0%, #1b1b1b 100%);
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0 0.8rem;
    padding-top: 0.5rem;
}

.preview-browser-left {
    display: flex;
    align-items: center;
    gap: 0.45rem;

    span {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.16);
        display: inline-block;
    }
}

.preview-browser-tab {
    height: 26px;
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    width: 150px;
    border-radius: 0.5rem 0.5rem 0 0;
    background: rgba(255, 255, 255, 0.06);
}

.preview-browser-circle {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    margin-right: 0.4rem;
}

.preview-browser-pill {
    width: 88px;
    height: 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
}

.preview-form {
    width: min(520px, 100%);
    height: 40px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 0.8rem;
    display: flex;
    align-items: center;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.04);

    input {
        border: 0;
        outline: 0;
        width: 100%;
        height: 100%;
        background: transparent;
        color: #fff;
        padding: 0 0.9rem;
        font-size: 0.88rem;
    }

    button {
        border: 0;
        height: 100%;
        width: 42px;
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        font-size: 1rem;
        cursor: pointer;
    }
}

.preview-mode-toggle {
    display: flex;
    gap: 0.25rem;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 0.6rem;
    padding: 0.2rem;
}

.preview-mode-btn {
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.55);
    border-radius: 0.45rem;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s;

    &.active {
        background: rgba(255, 255, 255, 0.18);
        color: #fff;
    }

    &:hover:not(.active) {
        color: rgba(255, 255, 255, 0.8);
    }
}

.config-window-preview.is-mobile {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: unset;

    .preview-frame-wrap {
        width: 390px;
        max-width: 100%;
        height: calc(100% - 2rem);
        border-radius: 1.5rem;
        border: 3px solid #ededed;
        overflow: hidden;
        flex-shrink: 0;
        margin: 0 auto;

        iframe {
            width: 100%;
            height: 100%;
            border: 0;
            border-radius: 0;
        }
    }
}

.preview-frame-wrap {
    position: absolute;
    inset: 0;

    iframe {
        width: 100%;
        height: 100%;
        border: 0;
        background: #fff;
    }
}

.preview-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(7, 7, 7, 0.08) 0%, rgba(7, 7, 7, 0.2) 100%);
}

.preview-widget {
    position: absolute;
    right: 20px;
    bottom: 20px;
    width: 280px;
    border-radius: 1rem;
    background: #ffffff;
    border: 1px solid #dad7e0;
    padding: 0.9rem;
    box-shadow: 0 8px 24px rgba(12, 12, 12, 0.16);
    font-family: var(--widget-font-family);
    text-align: var(--widget-text-align);

    &.is-square {
        border-radius: 0.35rem;
    }

    &.is-rounded {
        border-radius: 1rem;
    }

    &.is-squircle {
        border-radius: 1.5rem;
    }

    strong {
        display: block;
        margin-bottom: 0.35rem;
        color: #1d1d1f;
    }

    p {
        margin: 0;
        color: #4d4c54;
        font-size: 0.88rem;
    }
}

.preview-avatar {
    width: 42px;
    height: 42px;
    border-radius: 0.55rem;
    object-fit: cover;
    margin-bottom: 0.45rem;
    border: 1px solid #ded9e8;
}

.preview-messages {
    margin-top: 0.65rem;
    display: grid;
    gap: 0.35rem;

    span {
        background: var(--widget-chat-bubble-color);
        color: #fff;
        padding: 0.4rem 0.55rem;
        border-radius: 0.65rem;
        font-size: 0.76rem;
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        width: fit-content;
        max-width: 100%;
    }
}

.preview-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #222;
    font-size: 0.9rem;
    background: rgba(255, 255, 255, 0.7);
}

.preview-note {
    margin: 1.5rem;
    margin-top: unset;
    color: #656271;
    font-size: 0.7rem;
}
:global(:root) {
    --widget-color-type: solid;
    --widget-color-format: hsl;
    --widget-color-solid: hsl(220 16% 16%);
    --widget-color-gradient-start: hsl(220 16% 16%);
    --widget-color-gradient-end: hsl(220 16% 30%);
    --widget-trigger-background: hsl(220 16% 16%);
    --widget-trigger-size: 50px;
    --widget-trigger-shadow: 0 4px 12px rgba(18, 18, 24, 0.2);
    --widget-trigger-shadow-hover: 0 6px 16px rgba(18, 18, 24, 0.28);
    --widget-avatar: "";
    --widget-corner-style: rounded;
    --widget-corner-radius: 12px;
    --widget-panel-bg: #ffffff;
    --widget-panel-text: hsl(224 16% 20%);
    --widget-panel-border: hsl(220 16% 88%);
    --widget-panel-muted: hsl(224 10% 42%);
    --widget-body-border: hsl(220 16% 88%);
    --widget-body-bg: #ffffff;
    --widget-gradient-conversation-bg: rgba(255, 255, 255, 0.6);
    --widget-gradient-conversation-text: hsl(224 10% 36%);
    --widget-gradient-conversation-hover-bg: rgba(255, 255, 255, 0.86);
    --widget-gradient-conversation-hover-text: hsl(224 16% 20%);
    --widget-gradient-conversation-border: rgba(74, 92, 180, 0.2);
    --widget-welcome-title: "Hello";
    --widget-welcome-message: "How can I help you today?";
    --widget-starter-message: "Hello, how can I help you?";
    --widget-theme: dark;
    --widget-chat-bubble-color: #1e1e1e;
    --widget-user-bubble-color: hsl(217 91% 60%);
    --widget-chat-bubble-text-color: #ffffff;
    --widget-user-bubble-text-color: #ffffff;
    --widget-font-family: "Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif;
    --widget-text-align: left;
    --widget-floating-right: 24px;
    --widget-floating-left: auto;
    --widget-floating-bottom: 24px;
}

.vs__dropdown-toggle {
    border: 1px solid #d7d3de;
    border-radius: 0.7rem;
    min-height: 40px;
}

.vs__search,
.vs__selected,
.vs__dropdown-option {
    font-size: 0.84rem;
}

.vs__dropdown-menu {
    border-radius: 0.65rem;
    border: 1px solid #d7d3de;
    z-index: 4500;
}

.vs__dropdown-option--highlight {
    background: hsl(217 91% 60%);
}
@media (max-width: 1120px) {
    .config {
        flex-direction: column;
        height: auto;
    }

    .config-settings {
        max-width: none;
    }

    .config-window {
        min-height: 680px;
    }
}

.widget-trigger {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    right: var(--widget-floating-right, 24px);
    left: var(--widget-floating-left, auto);
    bottom: var(--widget-floating-bottom, 24px);
    z-index: 3;
    &-btn {
        width: var(--widget-trigger-size);
        height: var(--widget-trigger-size);
        border-radius: 50%;
        border: 1px solid transparent;
        display: inline-grid;
        place-items: center;
        background: var(--widget-trigger-background);
        color: #fff;
        box-shadow: var(--widget-trigger-shadow);
        transition:
            background-color 0.3s ease,
            box-shadow 0.3s ease,
            transform 0.2s ease;
        cursor: pointer;
        &:hover {
            box-shadow: var(--widget-trigger-shadow-hover);
            transform: translateY(-1px);
        }
    }
    &-label {
        background-color: #fff;
        padding: 0.5rem 1rem;
        border-radius: var(--widget-corner-radius, 12px);
        border: 2px solid #ededed;
        box-shadow: 0 0 #ededed;
        font-size: 0.85rem;
        color: #333;
        z-index: 2061;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        font-family: var(--widget-font-family);

        &.is-square,
        &.is-rounded {
            clip-path: none;
        }

        &.is-squircle {
            border-radius: 0;
            clip-path: polygon(
                50% 0%,
                64% 1%,
                76% 4%,
                86% 10%,
                94% 18%,
                98% 30%,
                100% 50%,
                98% 70%,
                94% 82%,
                86% 90%,
                76% 96%,
                64% 99%,
                50% 100%,
                36% 99%,
                24% 96%,
                14% 90%,
                6% 82%,
                2% 70%,
                0% 50%,
                2% 30%,
                6% 18%,
                14% 10%,
                24% 4%,
                36% 1%
            );
        }

        &.bubble-arrow {
            position: relative;
            &::after {
                content: "";
                position: absolute;
                bottom: 0;
                left: 98%;
                transform: rotate(35deg);
                border-width: 6px;
                border-style: solid;
                border-color: transparent transparent transparent #ededed;
            }
        }
    }
    &-mascot {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
    }
}
.widget-body {
    position: absolute;
    right: var(--widget-floating-right, 24px);
    left: auto;
    bottom: calc(var(--widget-floating-bottom, 24px) + 62px);
    width: min(360px, calc(100% - 24px));
    padding: 0.95rem;
    background: var(--widget-body-bg);
    border: 1px solid var(--widget-body-border);
    border-radius: var(--widget-corner-radius, 12px);
    box-shadow: 0 20px 38px rgba(8, 9, 14, 0.24);
    font-family: var(--widget-font-family);
    height: 100%;
    max-height: 50vh;
    min-height: 400px;
    display: flex;
    flex-direction: column;

    &.align-left {
        .widget-body-header {
            text-align: left;
            align-items: flex-start;
        }
    }

    &.align-center {
        .widget-body-header {
            text-align: center;
            align-items: center;
        }
    }

    &.align-right {
        .widget-body-header {
            text-align: right;
            align-items: flex-end;
        }
    }

    &.is-square {
        border-radius: 0;
    }

    &.is-rounded {
        border-radius: 12px;
    }

    &.is-squircle {
        border-radius: 24px;
        clip-path: none;
    }
}

.widget-body-header {
    background: var(--widget-header-bg);
    padding: 1rem;
    color: var(--widget-header-text);
    border-radius: var(--widget-inner-corner-radius);
    min-height: 40%;
    margin: -0.5rem;
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    .widget-avatar {
        width: 64px;
        height: 64px;
        object-fit: cover;
        margin-bottom: 0.5rem;
    }
    .widget-welcome-title {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }
    .widget-welcome-message {
        margin: 0;
        margin-bottom: 1rem;
        font-size: 0.9rem;
        color: var(--widget-header-muted);
    }

    &.is-color-gradient {
        align-items: center;
        text-align: center;

        .widget-avatar {
            margin-left: auto;
            margin-right: auto;
        }
    }
}

.widget-body.is-color-gradient {
    border: unset;

    .widget-body-header {
        text-align: center;
        align-items: center;

        .widget-avatar {
            margin-left: auto;
            margin-right: auto;
        }
    }
}

.widget-body-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.25rem;
    margin-right: -0.25rem;
}

.widget-body-footer {
    margin-top: 0.75rem;
}

.widget-body-footer-row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: 0.5rem;
}

.widget-body-footer-input {
    width: 100%;
    border: 1px solid var(--widget-panel-border);
    border-radius: var(--widget-inner-corner-radius);
    background-color: var(--widget-panel-bg);
    color: var(--widget-panel-text);
    padding: 0.65rem 0.75rem;
    font-size: 0.82rem;
    line-height: 1.35;
    resize: none;
    min-height: 40px;
    max-height: 120px;
    overflow-y: hidden;
    outline: none;

    &::placeholder {
        color: var(--widget-panel-muted);
    }
}

.widget-body-footer-send {
    width: 38px;
    height: 38px;
    border-radius: var(--widget-inner-corner-radius);
    border: 1px solid transparent;
    display: inline-grid;
    place-items: center;
    background: var(--widget-user-bubble-color);
    color: var(--widget-user-bubble-text-color);
    cursor: pointer;
}

.widget-body-footer-note {
    margin: 0.45rem 0 0;
    font-size: 0.65rem;
    line-height: 1.3;
    color: var(--widget-panel-muted);
    text-align: center;

    a {
        color: inherit;
        text-decoration: underline;
        opacity: 0.8;

        &:hover {
            opacity: 1;
        }
    }
}

.widget-body.align-left {
    .conversation-message-item,
    .widget-body-footer-input {
        text-align: left;
    }
}

.widget-body.align-center {
    .conversation-message-item,
    .widget-body-footer-input {
        text-align: center;
    }
}

.widget-body.align-right {
    .conversation-message-item,
    .widget-body-footer-input {
        text-align: right;
    }
}

.widget-body-starter {
    margin-top: 0.45rem;
    width: 100%;
    border: 0;
    border-radius: 0.7rem;
    padding: 0.55rem 0.7rem;
    background: var(--widget-user-bubble-color);
    color: #fff;
    font-size: 0.8rem;
    text-align: left;
}

.widget-body-fade-enter-active,
.widget-body-fade-leave-active {
    transition: all 0.24s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.widget-body-fade-enter-from,
.widget-body-fade-leave-to {
    opacity: 0;
    transform: translateY(14px) scale(0.96);
}

.conversation-message-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    &.is-color-gradient {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.6rem;
        width: 100%;
    }
}
.conversation-message-item {
    background-color: #fff;
    color: #333;
    padding: 0.5rem 1.5rem 0.5rem 0.75rem;
    border-radius: var(--widget-inner-corner-radius);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    position: relative;
    font-size: 0.75rem;
    line-height: 1rem;
    svg {
        flex-shrink: 0;
    }
    &::after {
        content: "";
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256'%3E%3Crect width='256' height='256' fill='none'/%3E%3Cpolyline points='96 48 176 128 96 208' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='16'/%3E%3C/svg%3E");
        position: absolute;
        top: 50%;
        right: 0.5rem;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
    }
    &:is(:hover, :focus) {
        background-color: #f4f4f4;
        color: #000;
    }

    .conversation-message-list.is-color-gradient & {
        background-color: var(--widget-gradient-conversation-bg);
        color: var(--widget-gradient-conversation-text);
        border: 1px solid var(--widget-gradient-conversation-border);
        backdrop-filter: blur(15px);
        -webkit-backdrop-filter: blur(15px);
        flex-direction: column;
        align-items: flex-start;
        gap: 0.35rem;
        font-size: 0.7rem;
        text-align: left;
        padding: 0.5rem;
        &::after {
            display: none;
        }

        &:is(:hover, :focus) {
            background-color: var(--widget-gradient-conversation-hover-bg);
            color: var(--widget-gradient-conversation-hover-text);
        }
    }
}

.chat-message {
    display: flex;
    padding-top: 1rem;
    gap: 0.75rem;
    &-avatar {
        width: 35px;
        height: 35px;
        flex-shrink: 0;
        img {
            width: 100%;
            height: 100%;
            border-radius: 100%;
            object-fit: cover;
        }
    }
    .chat-message-data {
        &-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.3rem;
            .chat-name {
                font-size: 0.9rem;
                font-weight: 600;
                margin-bottom: unset;
            }
            .chat-time {
                font-size: 0.7rem;
                color: #ededed;
            }
        }
        &-inner {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            .message-data {
                .card-stat {
                    margin-top: 0.5rem;
                    padding: 0.5rem 0.75rem;
                    max-width: 240px;
                    white-space: pre-wrap;
                    word-break: break-word;
                    margin-left: auto;
                    &-icon {
                        width: 30px;
                        height: 30px;
                        font-size: 1.3rem;
                    }
                }
            }
            .message-text {
                background-color: var(--widget-chat-bubble-color);
                color: var(--widget-chat-bubble-text-color);
                border-radius: var(--widget-bubble-corner-radius);
                padding: 0.5rem 0.75rem;
                font-size: 0.75rem;
                position: relative;
                &::after {
                    content: "";
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    left: -7px;
                    width: 0;
                    height: 0;
                    border-style: solid;
                    border-top: 8px solid transparent;
                    border-bottom: 8px solid transparent;
                    border-right: 8px solid var(--widget-chat-bubble-color);
                    border-left: 0;
                }
                p {
                    margin: unset;
                }
            }
        }
        &:has(.chat-message-faq) {
            width: 100%;
            .chat-message-inner {
                width: 100%;
                .message-data {
                    width: 100%;
                }
            }
        }
    }
    &.sender {
        flex-direction: row-reverse;
        .chat-message-data {
            &-header {
                flex-direction: row-reverse;
            }
            &-inner {
                flex-direction: row-reverse;
                .message-text {
                    background-color: var(--widget-user-bubble-color);
                    color: var(--widget-user-bubble-text-color);
                    border-radius: var(--widget-bubble-corner-radius);
                    &::after {
                        border-right: 0;
                        border-left: 8px solid var(--widget-user-bubble-color);
                        left: unset;
                        right: -7px;
                    }
                }
            }
        }
    }

    .widget-body.bubble-arrow-off & {
        .message-text {
            &::after {
                display: none;
            }
        }
    }
}
.config-banner {
    background-color: #fff;
    border: 1px solid #ededed;
    border-radius: 1rem;
    margin: 1rem;
    padding: 1rem;
    color: #000;
}

/* Trigger btn open state */
.widget-trigger-btn.is-open {
    transform: rotate(0deg);
    transition:
        background-color 0.3s ease,
        box-shadow 0.3s ease,
        transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Responsive — tablet */
@media (max-width: 900px) {
    .config-banner {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.65rem;
    }

    .config-banner-actions {
        width: 100%;
    }

    .config-banner-actions .banner-btn {
        flex: 1 1 auto;
        text-align: center;
    }

    .config-window-header {
        flex-wrap: wrap;
        gap: 0.5rem;
        padding: 0.75rem;
        min-height: unset;
    }

    .preview-form {
        width: 100%;
    }
}

/* Responsive — mobile */
@media (max-width: 640px) {
    .config-banner {
        margin: 0.5rem;
    }

    .config {
        margin: 0.5rem;
        gap: 0.5rem;
    }

    .config-settings {
        padding: 0.85rem;
    }

    .config-window {
        min-height: 520px;
    }

    .config-window-preview {
        margin: 0.75rem;
    }

    .config-window-preview.is-mobile .preview-frame-wrap {
        width: 100%;
        border-radius: 0.75rem;
    }

    .font-card-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .avatar-grid {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }

    .corner-options {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .conversation-item {
        grid-template-columns: 35px 1fr;

        .remove-btn {
            grid-column: 1 / -1;
        }
    }
}

/* Mobile: trigger gizle widget açıkken */
.config-window-preview.is-mobile .widget.widget-is-open .widget-trigger {
    display: none;
}

/* Widget body full-screen inside mobile preview */
.config-window-preview.is-mobile .widget-body {
    right: 0 !important;
    left: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    max-height: 100%;
    min-height: 0;
    height: 100%;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
}

.widget-body-close {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;

    [dir="rtl"] & {
        right: auto;
        left: 0.75rem;
    }
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid var(--widget-panel-border);
    background: var(--widget-panel-bg);
    color: var(--widget-panel-muted);
    display: inline-grid;
    place-items: center;
    cursor: pointer;
    z-index: 4;
    transition:
        background 0.15s,
        color 0.15s;

    &:hover {
        background: var(--widget-panel-border);
        color: var(--widget-panel-text);
    }
}
</style>
